import * as React from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/api/api';
import { queryKeys } from '@/libs/queryKeys';
import { reportError } from '@/utils/reportError';
import { mapToUiMessage } from '@/utils/messageMapping';
import { useChatStore } from '@/store/chatStore';
import type {
  Message as BaseMessage,
  AudioChatResponse,
  ChatHistoryResponse,
  ConversationWithUnread,
} from '@/types';
import { MessageSender } from '@/types';
import type { UseChatOptions, UseChatReturn } from './types';
import { useAuth } from './useAuth';
import type { Message as SharedMessage } from '@/types';
import { analytics } from '@/utils/analytics';
import { useConversationStore } from '@/store/conversationStore';
import { useAudioStore } from '@/store/audioStore';
import { useToast } from '@/context/ToastContext';
import { useUiStore } from '@/store/uiStore';
import { billingRecoveryContext } from '@/utils/billingRecovery';

/**
 * Interface for API Errors with status code
 */
interface ApiErrorWithStatus {
  status?: number;
  message?: string;
  detail?: unknown;
}

/**
 * Extended Message interface for frontend state.
 */
export interface Message extends BaseMessage {
  audioBuffer?: string[];
  isAudioReady?: boolean;
  ttsEnabled?: boolean;
  ttsFailed?: boolean;
  isTextComplete?: boolean;
  isAllAudioStreamed?: boolean;

  // Local user audio message fields
  localAudioUrl?: string;
  waveform?: number[];
  duration?: number;
}

/**
 * Interface for Server-Sent Events (SSE) data payload.
 */
interface SSEEventData {
  chunk?: string;
  content?: string;
  message_id?: string;
  created_at?: string;
  audio_url?: string;
  suggested_questions?: Array<{ id: string; text: string }>;
  message?: string; // Error message structure 1
  error?: string; // Error message structure 2
  code?: string;
  capability?: string;
  required?: number;
  balance?: number;
  detail?: string | object; // Generic error structure
  is_read?: boolean; // Unread tracking
  audio_generation_skipped?: boolean; // optional flag in SSE end event
}

interface SSEEvent {
  event: string;
  data: string | SSEEventData;
}

type VideoStatus = NonNullable<BaseMessage['video_status']>;
const VIDEO_STATUSES: ReadonlySet<VideoStatus> = new Set([
  'pending',
  'processing',
  'done',
  'failed',
]);
const isVideoStatus = (value: string): value is VideoStatus =>
  VIDEO_STATUSES.has(value as VideoStatus);

/**
 * Cache shape for one infinite-query page. Same fields as `ChatHistoryResponse`
 * but with `Message[]` (UI shape) instead of `MessageResponse[]` — `queryFn`
 * runs `mapToUiMessage` once on each fetch so consumers + writers don't have
 * to cast at every site.
 */
type ChatHistoryPage = Omit<ChatHistoryResponse, 'messages'> & {
  messages: Message[];
  pageParam: number;
};

/**
 * Removes orphan recovery typing placeholders. The `-recovery` suffix tags bubbles created by
 * `checkGenerationStatus` to represent in-flight server generations on chat re-entry; they are
 * cleared once the underlying generation completes or a new send supersedes them.
 */
const stripRecoveryPlaceholders = (msgs: Message[]): Message[] =>
  msgs.filter((msg) => !msg.id.endsWith('-recovery'));

// Module-level: AbortController handles aren't React state and don't need to drive
// re-renders, so they stay outside the store. Background streams continue updating
// the chat store after their originating hook instance unmounts.
const globalAbortControllers = new Map<string, AbortController>();

// Stable empty array so the activeMessages selector returns the same reference when
// no entry exists for an echo — otherwise `?? []` would mint a new array on every
// store change and force a re-render.
const EMPTY_ACTIVE_MESSAGES: Message[] = [];

/**
 * Aborts every in-flight chat stream and wipes per-echo chat state.
 * Must be called on logout so a subsequent login doesn't inherit the previous user's
 * placeholders, greeting flags, or abort controllers.
 */
export const resetChatModuleState = (): void => {
  globalAbortControllers.forEach((controller) => controller.abort());
  globalAbortControllers.clear();
  useChatStore.getState().reset();
};

/**
 * Custom hook for managing chat logic, history loading via useInfiniteQuery, and real-time SSE streaming.
 */
export function useChat({
  echoKey,
  ttsProvider = 'elevenlabs',
  generateAudio,
}: UseChatOptions): UseChatReturn {
  const { refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const openBillingRecovery = useUiStore((state) => state.openBillingRecovery);

  // Store abort controllers per echo to avoid cross-echo cancellation
  const abortControllersRef = React.useRef(globalAbortControllers);

  // Use local Set instead of global to ensure read status is updated on every chat entry.
  // Since ChatView remounts on echo switch (key={echoId}), this resets correctly.
  const markedAsReadEchoes = React.useRef(new Set<string>());

  const echoId = echoKey;
  const PAGE_SIZE = 20;

  // Store total count per echo to avoid dependency issues
  const totalCountRef = React.useRef<Map<string, number>>(new Map());

  // 1. Metadata Query (total count) to position history starting from latest messages
  const { data: metaData, isFetched: isMetaFetched } = useQuery({
    queryKey: ['chat', 'meta', echoId],
    queryFn: async ({ signal }) => {
      const result = await api.chat.getHistory(echoId, 0, 1, signal);
      totalCountRef.current.set(echoId, result.total);
      return result;
    },
    enabled: !!echoId,
    staleTime: 30 * 1000, // 30s — reuse cached total on rapid echo switching
    refetchOnMount: true, // refetch only if stale (> 30s since last fetch)
    refetchOnWindowFocus: true, // always refetch on tab focus for cross-tab sync
  });

  // 2. History Query (Infinite - starts from newest chunk)
  // Gate history query on meta fetch to ensure correct total count before calculating offset
  const isHistoryQueryEnabled = !!echoId && isMetaFetched;

  const {
    data: historyData,
    fetchNextPage,
    hasNextPage,
    isLoading: isHistoryLoading,
  } = useInfiniteQuery({
    queryKey: queryKeys.chat.history(echoId),
    queryFn: async ({ pageParam, signal }) => {
      const currentTotal = totalCountRef.current.get(echoId) ?? 0;

      // If pageParam is -1 (initial sentinel), calculate offset for the latest messages.
      // Otherwise, use the provided pageParam.
      let currentOffset = pageParam as number;
      if (currentOffset === -1) {
        currentOffset = Math.max(0, currentTotal - PAGE_SIZE);
      }

      const res = await api.chat.getHistory(echoId, currentOffset, PAGE_SIZE, signal);
      const page: ChatHistoryPage = {
        ...res,
        messages: res.messages.map(mapToUiMessage),
        pageParam: currentOffset,
      };
      return page;
    },
    getNextPageParam: (lastPage: ChatHistoryPage) => {
      const currentOffset = lastPage.pageParam ?? 0;
      if (currentOffset === 0) return undefined;
      const nextOffset = Math.max(0, currentOffset - PAGE_SIZE);
      return nextOffset === currentOffset ? undefined : nextOffset;
    },
    // Use -1 as a sentinel so queryFn can compute the latest offset after meta is fetched
    initialPageParam: -1,
    enabled: isHistoryQueryEnabled,
    staleTime: Infinity,
  });

  // Invalidate stale history cache when server total diverges from locally tracked total.
  // This prevents pagination drift caused by incrementMetaCount bumping the local total
  // while the infinite query cache retains pages computed from the old total.
  const prevMetaTotalRef = React.useRef<Map<string, number>>(new Map());
  React.useEffect(() => {
    if (!isMetaFetched || !metaData) return;
    const serverTotal = metaData.total;
    const prevTotal = prevMetaTotalRef.current.get(echoId);

    if (prevTotal !== undefined && prevTotal !== serverTotal) {
      // Server total differs from what we last knew — refetch history in background
      // Using invalidateQueries (not removeQueries) to keep old data visible during refetch
      prevMetaTotalRef.current.set(echoId, serverTotal);
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.history(echoId) });

      if (serverTotal > prevTotal) {
        const current = useChatStore.getState().getActiveMessages(echoId);
        const cleaned = stripRecoveryPlaceholders(current);
        if (cleaned.length !== current.length) {
          useChatStore.getState().setActiveMessages(echoId, cleaned);
        }
      }
    } else {
      prevMetaTotalRef.current.set(echoId, serverTotal);
    }
  }, [isMetaFetched, metaData, echoId, queryClient]);

  // Active/optimistic messages for this echo. Subscribed via Zustand selector so
  // background streams and other hook instances trigger re-renders automatically —
  // no window-event sync needed.
  const activeMessages = useChatStore(
    (state) => state.activeMessages[echoId] ?? EMPTY_ACTIVE_MESSAGES
  );

  /** Update the active-message slice for the current echo. Wraps the store action. */
  const updateActiveMessages = React.useCallback(
    (updater: (prev: Message[]) => Message[]) => {
      useChatStore.getState().setActiveMessages(echoId, updater);
    },
    [echoId]
  );

  const [error, setError] = React.useState<string | null>(null);
  const [isSending, setIsSending] = React.useState(false);

  // 3. Derived State: Combined Messages
  // History is fetched Newest->Oldest (Pages: [0: Newest 20, 1: Older 20...])
  // UI needs [Oldest ... Newest]
  const messages = React.useMemo(() => {
    const historyFlat = historyData?.pages.flatMap((page: ChatHistoryPage) => page.messages) || [];
    // Chronological (Old -> New)
    const historySorted = [...historyFlat].sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
    );
    // Append active messages (already chronological), deduplicating a streamed
    // placeholder against its persisted history row.
    const combined = [...historySorted, ...activeMessages];
    const seen = new Set<string>();
    return combined.filter((msg) => {
      const identity = msg.serverMessageId ?? msg.id;
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    });
  }, [historyData, activeMessages]);

  React.useEffect(() => {
    useAudioStore.getState().stop();
    return () => {
      useAudioStore.getState().stop();
    };
  }, [echoId]);

  // Mark conversation as read when entering chat
  const markReadMutation = useMutation({
    mutationFn: (targetEchoId: string) => api.chat.markConversationAsRead(targetEchoId),
    onSuccess: (_data, targetEchoId) => {
      if (!targetEchoId) return;
      useConversationStore.getState().markAsRead(targetEchoId);
      // Surgically update the conversations cache so the UI reflects the
      // read state without triggering a full /conversations refetch.
      // Previously this was `invalidateQueries`, which fired one full list
      // refetch per chat-open and turned routine navigation into a storm.
      queryClient.setQueryData<ConversationWithUnread[]>(['chat', 'conversations'], (prev) =>
        prev?.map((conv) => (conv.echo_id === targetEchoId ? { ...conv, unread_count: 0 } : conv))
      );
    },
    onError: () => {
      // Silent fail — mark-read is non-critical
    },
  });

  // Stable ref for markReadMutation to avoid unnecessary effect re-fires
  const markReadMutationRef = React.useRef(markReadMutation);
  React.useEffect(() => {
    markReadMutationRef.current = markReadMutation;
  }, [markReadMutation]);

  // Call mark-read when chat is loaded (only once per conversation per session)
  React.useEffect(() => {
    if (echoId && !markedAsReadEchoes.current.has(echoId)) {
      markedAsReadEchoes.current.add(echoId);
      markReadMutationRef.current.mutate(echoId);
    }
  }, [echoId]);

  // Check generation status when entering chat
  React.useEffect(() => {
    const checkGenerationStatus = async () => {
      if (!echoId) return;

      // If we already have active messages for this echo, skip the check
      const existingActiveMessages = useChatStore.getState().getActiveMessages(echoId);
      if (existingActiveMessages.length > 0) {
        return;
      }

      try {
        const status = await api.chat.getGenerationStatus(echoId);

        // Double-check active messages to handle race condition where sendMessage fired while status was fetching
        const currentActive = useChatStore.getState().getActiveMessages(echoId);
        if (currentActive.length > 0) {
          return;
        }

        // Check if the "generating" task corresponds to a message we already have in history
        // (Handles race condition where stream finished but backend status is still cached as generating)
        if (status.is_generating && status.started_at) {
          const latestHistory = queryClient.getQueryData<InfiniteData<ChatHistoryPage>>(
            queryKeys.chat.history(echoId)
          );
          const latestMsg = latestHistory?.pages?.[0]?.messages?.[0];

          if (latestMsg) {
            const statusTime = new Date(status.started_at).getTime();
            const msgTime = new Date(latestMsg.created_at).getTime();
            // If the latest message was created after the generation started, we assume it's the result of that generation
            if (msgTime >= statusTime) {
              return;
            }
          }
        }

        if (status.is_generating) {
          const aiPlaceholder: Message = {
            id: `temp-ai-${Date.now()}-recovery`,
            content: '',
            sender_type: MessageSender.AI,
            created_at: status.started_at ?? new Date().toISOString(),
            audioBuffer: [],
            isAudioReady: false,
            ttsEnabled: true,
            ttsFailed: false,
            isTextComplete: false,
            isAllAudioStreamed: false,
          };

          useChatStore.getState().setActiveMessages(echoId, [aiPlaceholder]);
        }
      } catch (err) {
        reportError('useChat.recoverGenerationStatus', err);
      }
    };

    checkGenerationStatus();
  }, [echoId, queryClient]); // Added queryClient to dependencies

  // Helper to update Last Message Cache
  const updateLastMessageCache = React.useCallback(
    (targetEchoId: string, message: SharedMessage) => {
      queryClient.setQueryData(queryKeys.chat.lastMessage(targetEchoId), message);
    },
    [queryClient]
  );

  // Helper to manually update the Infinite Query Cache safely (prepend to newest page)
  const updateHistoryCache = React.useCallback(
    (targetEchoId: string, updater: (page: ChatHistoryPage) => ChatHistoryPage) => {
      queryClient.setQueryData<InfiniteData<ChatHistoryPage>>(
        queryKeys.chat.history(targetEchoId),
        (oldData) => {
          if (!oldData || !oldData.pages) return oldData;

          // Page 0 is the newest (queryFn computes the initial offset for the latest chunk).
          const newPages = [...oldData.pages];
          if (newPages.length > 0) {
            const first = newPages[0];
            if (first) {
              newPages[0] = updater(first);
            } else {
              return oldData;
            }
          }

          return {
            ...oldData,
            pages: newPages,
          };
        }
      );
    },
    [queryClient]
  );

  // Update meta cache to prevent greeting retry race conditions.
  const incrementMetaCount = React.useCallback(
    (targetEchoId: string) => {
      queryClient.setQueryData<{ total: number }>(['chat', 'meta', targetEchoId], (old) => {
        const newTotal = (old?.total ?? 0) + 1;
        // Sync refs so drift detection treats local increments as expected
        totalCountRef.current.set(targetEchoId, newTotal);
        prevMetaTotalRef.current.set(targetEchoId, newTotal);
        return { ...old, total: newTotal };
      });
    },
    [queryClient]
  );

  // Store current echoId in a ref so SSE handlers can check it without closure issues
  const currentEchoIdRef = React.useRef(echoId);
  React.useEffect(() => {
    currentEchoIdRef.current = echoId;
  }, [echoId]);

  // SSE Handler Factory
  const createSSEEventHandler = React.useCallback(
    (aiTempId: string, messageEndReceivedRef: { current: boolean }, targetEchoId: string) => {
      let accumulatedText = '';

      // Helper to update messages for the target echo (not necessarily the current one)
      const updateTargetEchoMessages = (updater: (prev: Message[]) => Message[]) => {
        const currentMessages = useChatStore.getState().getActiveMessages(targetEchoId);
        const updated = updater(currentMessages);
        useChatStore.getState().setActiveMessages(targetEchoId, updated);
      };

      const handlers: Record<string, (evt: SSEEvent) => void> = {
        keepalive: () => {
          // no-op
        },
        message_start: () => {
          // no-op
        },
        audio_start: () => {
          analytics.audioPlaybackStarted(targetEchoId, aiTempId, ttsProvider);
        },
        audio_chunk: (evt) => {
          const data = evt.data as SSEEventData;
          const chunk = data?.chunk;
          if (chunk) {
            updateTargetEchoMessages((prev) =>
              prev.map((msg) =>
                msg.id === aiTempId && msg.audioBuffer
                  ? { ...msg, audioBuffer: [...msg.audioBuffer, chunk] }
                  : msg
              )
            );
          }
        },
        audio_end: () => {
          updateTargetEchoMessages((prev) =>
            prev.map((msg) => (msg.id === aiTempId ? { ...msg, isAudioReady: true } : msg))
          );
        },
        all_audio_complete: () => {
          updateTargetEchoMessages((prev) =>
            prev.map((msg) => (msg.id === aiTempId ? { ...msg, isAllAudioStreamed: true } : msg))
          );
        },
        text_chunk: (evt) => {
          let text = '';
          if (typeof evt.data === 'string') text = evt.data;
          else {
            const data = evt.data as SSEEventData;
            text = data?.content ?? '';
          }
          if (text) {
            accumulatedText += text;
            updateTargetEchoMessages((prev) =>
              prev.map((msg) => (msg.id === aiTempId ? { ...msg, content: accumulatedText } : msg))
            );
          }
        },
        text_complete: (evt) => {
          const data = evt.data as SSEEventData;
          accumulatedText = data?.content ?? accumulatedText;
          updateTargetEchoMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiTempId
                ? {
                    ...msg,
                    content: accumulatedText,
                    isTextComplete: true,
                    ...(data?.message_id ? { serverMessageId: data.message_id } : {}),
                    ...(data?.created_at ? { created_at: data.created_at } : {}),
                    ...(data?.suggested_questions
                      ? { suggested_questions: data.suggested_questions }
                      : {}),
                  }
                : msg
            )
          );
        },
        audio_error: () => {
          updateTargetEchoMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiTempId
                ? {
                    ...msg,
                    has_audio: false,
                    isAudioGenerated: false,
                    ttsFailed: true,
                    ttsEnabled: false,
                    isAllAudioStreamed: true,
                  }
                : msg
            )
          );
        },
        message_end: (evt) => {
          messageEndReceivedRef.current = true;
          const data = evt.data as SSEEventData;
          const messageId = data?.message_id ?? aiTempId;
          const createdAt = data?.created_at ?? new Date().toISOString();
          const finalContent = data?.content ?? accumulatedText;
          const audioUrl = data?.audio_url;
          const suggestedQuestions = data?.suggested_questions;
          const isRead = data?.is_read;
          const activeStreamingMessage = useChatStore
            .getState()
            .getActiveMessages(targetEchoId)
            .find((msg) => msg.id === aiTempId);
          const completedAudioBuffer = activeStreamingMessage?.audioBuffer ?? [];
          const audioGenerationSkipped =
            (data as unknown as AudioChatResponse)?.audio_generation_skipped ?? false;
          const hasCompletedAudio =
            !audioGenerationSkipped && (!!audioUrl || completedAudioBuffer.length > 0);

          const rawVideoStatus = (data as Record<string, unknown>)['video_status'];
          const videoStatus: VideoStatus | undefined =
            typeof rawVideoStatus === 'string' && isVideoStatus(rawVideoStatus)
              ? rawVideoStatus
              : undefined;

          const finalizedMessage: Message = {
            id: messageId,
            clientMessageId: aiTempId,
            content: finalContent,
            sender_type: MessageSender.AI,
            created_at: createdAt,
            isTextComplete: true,
            isAllAudioStreamed: true,
            has_audio: hasCompletedAudio,
            isAudioGenerated: hasCompletedAudio,
            audioBuffer: completedAudioBuffer,
            ...(audioUrl ? { audio_url: audioUrl } : {}),
            ...(suggestedQuestions ? { suggested_questions: suggestedQuestions } : {}),
            ...(isRead !== undefined ? { is_read: isRead } : {}),
            ...(videoStatus ? { video_status: videoStatus } : {}),
            ...(activeStreamingMessage?.rating ? { rating: activeStreamingMessage.rating } : {}),
          };

          // Persist first, using the temporary ID as a stable React render key.
          // The combined-message selector deduplicates this row against the
          // active placeholder, so finalization updates the existing bubble
          // without unmounting and replaying its entrance animation.
          updateHistoryCache(targetEchoId, (page) => {
            // Skip if message already exists in cache (prevent duplicates)
            if (page.messages.some((msg) => msg.id === messageId)) {
              return page;
            }

            // Check the most recent message in the cache (User message from onMutate)
            const latestMsg = page.messages[0];

            // Fix Clock Skew: If AI (Server) time <= User (Client) time, bump AI time
            if (latestMsg) {
              const userTime = new Date(latestMsg.created_at).getTime();
              const aiTime = new Date(finalizedMessage.created_at).getTime();

              if (aiTime <= userTime) {
                finalizedMessage.created_at = new Date(userTime + 1).toISOString();
              }
            }

            return {
              ...page,
              messages: [finalizedMessage, ...page.messages],
            };
          });

          updateTargetEchoMessages((prev) => prev.filter((msg) => msg.id !== aiTempId));

          updateLastMessageCache(targetEchoId, finalizedMessage as SharedMessage);

          // Check if user is currently viewing this chat before incrementing unread count
          const isViewing = targetEchoId === currentEchoIdRef.current;

          // Update conversation list order for AI response/greeting via Zustand store
          useConversationStore.getState().moveToTop(
            targetEchoId,
            finalizedMessage.content,
            finalizedMessage.created_at,
            !isViewing // Only increment unread count if NOT viewing the chat
          );

          incrementMetaCount(targetEchoId);

          // Refresh echo stats (total messages, etc.) so StatsRow updates without page reload
          queryClient.invalidateQueries({ queryKey: queryKeys.stats.echo(targetEchoId) });

          setIsSending(false);
          refreshProfile();
        },
        error: (evt) => {
          let errMsg = 'Streaming error';
          let errorData: SSEEventData | undefined;
          if (typeof evt.data === 'string') errMsg = evt.data;
          else {
            errorData = evt.data as SSEEventData;
            if (errorData?.message) errMsg = errorData.message;
            else if (errorData?.error) errMsg = errorData.error;
            else if (errorData?.detail) errMsg = String(errorData.detail);
          }
          updateTargetEchoMessages((prev) => prev.filter((msg) => msg.id !== aiTempId));
          messageEndReceivedRef.current = true;

          if (targetEchoId === currentEchoIdRef.current) {
            if (
              errorData?.code === 'insufficient_credits' ||
              errorData?.error === 'Insufficient credits'
            ) {
              openBillingRecovery(billingRecoveryContext(errorData));
            } else {
              addToast({
                title: 'Message Blocked',
                subtitle: errMsg,
                type: 'error',
              });
            }
            setIsSending(false);
          }
        },
      };

      return (evt: SSEEvent) => {
        const handler = handlers[evt.event];
        if (handler) handler(evt);
      };
    },
    [
      ttsProvider,
      refreshProfile,
      updateLastMessageCache,
      updateHistoryCache,
      incrementMetaCount,
      addToast,
      queryClient,
      openBillingRecovery,
    ]
  );

  // Refactored Mutation for sending messages
  const sendMutation = useMutation({
    mutationFn: async ({
      content,
      aiTempId,
      targetEchoId,
      generateAudio: generateAudioParam,
      generateVideo: generateVideoParam,
    }: {
      content: string;
      userTempId: string;
      aiTempId: string;
      targetEchoId: string;
      generateAudio: boolean;
      generateVideo: boolean;
    }) => {
      // Abort only the controller for this specific echo
      const existingController = abortControllersRef.current.get(targetEchoId);
      if (existingController) {
        existingController.abort();
        abortControllersRef.current.delete(targetEchoId);
      }

      const controller = new AbortController();
      abortControllersRef.current.set(targetEchoId, controller);

      const messageEndReceivedRef = { current: false };

      try {
        const eventHandler = createSSEEventHandler(aiTempId, messageEndReceivedRef, targetEchoId);

        await api.chat.streamChat(
          targetEchoId,
          content,
          ttsProvider,
          (evt) => eventHandler(evt as SSEEvent),
          controller.signal,
          generateAudioParam,
          generateVideoParam
        );

        analytics.messageSent(targetEchoId, 'echo_chat', 'text');

        // Fallback polling if streaming fails silently
        if (!messageEndReceivedRef.current) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, 2000);
          });
          try {
            const meta = queryClient.getQueryData<{ total: number }>([
              'chat',
              'meta',
              targetEchoId,
            ]);
            const total = meta?.total || 1;
            const lastOffset = Math.max(0, total - 1);

            const response = await api.chat.getHistory(targetEchoId, lastOffset, 20);

            const fetchedMessages = response.messages;
            const lm = fetchedMessages[fetchedMessages.length - 1];
            if (lm) {
              const finalized = mapToUiMessage(lm);

              // Clear active temp for the target echo
              const currentMessages = useChatStore.getState().getActiveMessages(targetEchoId);
              const updated = currentMessages.filter((msg) => msg.id !== aiTempId);
              useChatStore.getState().setActiveMessages(targetEchoId, updated);

              updateHistoryCache(targetEchoId, (page) => {
                if (page.messages.some((msg) => msg.id === finalized.id)) {
                  return page;
                }

                const latestMsg = page.messages[0];
                if (latestMsg) {
                  const userTime = new Date(latestMsg.created_at).getTime();
                  const aiTime = new Date(finalized.created_at).getTime();
                  if (aiTime <= userTime) {
                    finalized.created_at = new Date(userTime + 1).toISOString();
                  }
                }
                return {
                  ...page,
                  messages: [finalized, ...page.messages],
                };
              });

              updateLastMessageCache(targetEchoId, finalized as SharedMessage);
              refreshProfile();
            }
          } catch (err) {
            reportError('useChat.streamFallback', err);
          }
        }
      } finally {
        // Clean up the controller for this echo only if it's still the same one
        if (abortControllersRef.current.get(targetEchoId) === controller) {
          abortControllersRef.current.delete(targetEchoId);
        }
      }
    },
    onMutate: async ({
      content,
      userTempId,
      aiTempId,
      targetEchoId,
      generateAudio: generateAudioParam,
    }) => {
      setError(null);
      setIsSending(true);

      // 1. Create AI placeholder (always)
      const aiPlaceholder: Message = {
        id: aiTempId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        audioBuffer: [],
        isAudioReady: false,
        ttsEnabled: true,
        ttsFailed: false,
        isTextComplete: false,
        isAllAudioStreamed: false,
        // Keep the play action visible when this specific request asked for TTS.
        // Clickability is derived separately from received audio bytes or a URL.
        has_audio: generateAudioParam,
        isAudioGenerated: false,
      };

      // 2. Set Local Active for target echo (for streaming visuals).
      const currentMessages = stripRecoveryPlaceholders(
        useChatStore.getState().getActiveMessages(targetEchoId)
      );
      const updatedMessages = [...currentMessages, aiPlaceholder];
      useChatStore.getState().setActiveMessages(targetEchoId, updatedMessages);

      // 3. PERSIST USER MESSAGE IMMEDIATELY (only if non-empty)
      if (content.trim()) {
        const userMessage: Message = {
          id: userTempId,
          content,
          sender_type: MessageSender.USER,
          created_at: new Date().toISOString(),
        };

        updateHistoryCache(targetEchoId, (page) => {
          if (page.messages.some((msg) => msg.id === userTempId)) {
            return page;
          }
          return {
            ...page,
            messages: [userMessage, ...page.messages],
          };
        });
        updateLastMessageCache(targetEchoId, userMessage as SharedMessage);

        incrementMetaCount(targetEchoId);

        // Update conversation list order for User message via Zustand store (no unread increment)
        useConversationStore
          .getState()
          .moveToTop(targetEchoId, content, userMessage.created_at, false);
      }
      // Intentionally skip updating conversation list when content is empty (greeting trigger)
      // to avoid a blank preview in the sidebar.

      // Always increment meta count — even for greetings (empty content).
      // This prevents the greeting effect from re-firing while the AI response is in-flight,
      // since it checks `metaData.total > 0` as one of its guards.
      if (!content.trim()) {
        incrementMetaCount(targetEchoId);
      }

      return { userTempId, aiTempId, targetEchoId };
    },
    onError: (err, variables, context) => {
      if ((err as Error).name === 'AbortError') return;

      const errorMsg = getErrorMessage(err);
      const apiError = err as ApiErrorWithStatus;
      const targetEchoId = variables.targetEchoId;

      analytics.messageSendFailed(targetEchoId, errorMsg);

      if (targetEchoId === currentEchoIdRef.current) {
        if (apiError.status === 402) {
          const fallbackCapability = variables.generateVideo
            ? 'VIDEO'
            : variables.generateAudio
              ? 'VOICE'
              : 'TEXT';
          openBillingRecovery(billingRecoveryContext(err, fallbackCapability));
        } else if (apiError.status === 400) {
          addToast({
            title: 'Message Blocked',
            subtitle: errorMsg,
            type: 'error',
          });
        } else if (apiError.status === 500) {
          addToast({
            title: 'Response Error',
            subtitle: "I'm not able to respond to that. Let's try a different topic.",
            type: 'error',
          });
        } else {
          addToast({
            title: 'Message Failed',
            subtitle: errorMsg,
            type: 'error',
          });
        }
      }

      // Rollback: Remove AI placeholder
      if (context) {
        const currentMessages = useChatStore.getState().getActiveMessages(targetEchoId);
        const updated = currentMessages.filter((msg) => msg.id !== context.aiTempId);
        useChatStore.getState().setActiveMessages(targetEchoId, updated);

        // Remove User message only if it was added (content not empty)
        if (variables.content.trim()) {
          updateHistoryCache(targetEchoId, (page) => ({
            ...page,
            messages: page.messages.filter((msg) => msg.id !== context.userTempId),
          }));
        }
      }
    },
    onSettled: () => {
      setIsSending(false);
    },
  });

  const sendMessage = React.useCallback(
    async (
      content: string,
      options?: { allowEmpty?: boolean; generateAudio?: boolean; generateVideo?: boolean }
    ) => {
      if (!content.trim() && !options?.allowEmpty) return;

      const userTempId = `temp-user-${Date.now()}`;
      const aiTempId = `temp-ai-${Date.now()}`;

      sendMutation.mutate({
        content,
        userTempId,
        aiTempId,
        targetEchoId: echoId,
        generateAudio: options?.generateAudio ?? generateAudio,
        generateVideo: options?.generateVideo ?? false,
      });
    },
    [sendMutation, echoId, generateAudio]
  );

  // Ref to track in-flight greeting request to prevent duplicate sends across re-renders
  const greetingInFlightRef = React.useRef<string | null>(null);
  // Stable ref for sendMutation to avoid unnecessary effect re-fires
  const sendMutationRef = React.useRef(sendMutation);
  React.useEffect(() => {
    sendMutationRef.current = sendMutation;
  }, [sendMutation]);

  // Auto-greeting trigger: only fire if history is fetched AND empty AND no active stream.
  React.useEffect(() => {
    if (!isMetaFetched) return;

    const hasHistory = (metaData?.total ?? 0) > 0;
    const hasActive = activeMessages.length > 0;
    const alreadySent = useChatStore.getState().hasGreetingSent(echoId);
    const inFlight = greetingInFlightRef.current === echoId;

    if (!hasHistory && !hasActive && !alreadySent && !inFlight) {
      // Mark as sent immediately to prevent race conditions or duplicate triggers
      useChatStore.getState().markGreetingSent(echoId);
      greetingInFlightRef.current = echoId;

      // Trigger backend greeting; handle AbortError specially
      const userTempId = `temp-user-${Date.now()}`;
      const aiTempId = `temp-ai-${Date.now()}`;
      sendMutationRef.current
        .mutateAsync({
          content: '',
          userTempId,
          aiTempId,
          targetEchoId: echoId,
          generateAudio,
          generateVideo: false,
        })
        .catch((err: unknown) => {
          // Only reset flag if NOT an AbortError
          const isAbort = err instanceof Error && err.name === 'AbortError';
          if (!isAbort) {
            useChatStore.getState().clearGreetingSent(echoId);
          }
        })
        .finally(() => {
          greetingInFlightRef.current = null;
        });
    }
  }, [metaData, echoId, activeMessages.length, isMetaFetched, generateAudio]);

  const sendAudioMessage = React.useCallback(
    async (blob: Blob, waveform: number[], duration: number, generateAudioForAI: boolean) => {
      setError(null);
      setIsSending(true);

      const tempId = `temp-audio-${Date.now()}`;
      const localUrl = URL.createObjectURL(blob);

      // 1. Create AI Placeholder for "Typing..." state
      const aiTempId = `temp-ai-audio-${Date.now()}`;
      const aiPlaceholder: Message = {
        id: aiTempId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        audioBuffer: [],
        isAudioReady: false,
        ttsEnabled: true,
        ttsFailed: false,
        isTextComplete: false, // Triggers typing animation
        isAllAudioStreamed: false,
        has_audio: generateAudioForAI,
        isAudioGenerated: false,
      };

      const currentActive = stripRecoveryPlaceholders(
        useChatStore.getState().getActiveMessages(echoId)
      );
      const updatedActive = [...currentActive, aiPlaceholder];
      useChatStore.getState().setActiveMessages(echoId, updatedActive);

      const optimisticMessage: Message = {
        id: tempId,
        content: '',
        sender_type: MessageSender.USER,
        created_at: new Date().toISOString(),
        is_audio_message: true,
        localAudioUrl: localUrl,
        waveform,
        duration,
        has_audio: false,
        isAudioGenerated: false,
      };

      updateHistoryCache(echoId, (page) => ({
        ...page,
        messages: [optimisticMessage, ...page.messages],
      }));
      updateLastMessageCache(echoId, optimisticMessage as SharedMessage);

      try {
        const response: AudioChatResponse = await api.chat.sendAudioMessage(
          echoId,
          blob,
          ttsProvider,
          generateAudioForAI
        );

        const activeAfterReq = useChatStore.getState().getActiveMessages(echoId);
        const cleanedActive = activeAfterReq.filter((msg) => msg.id !== aiTempId);
        useChatStore.getState().setActiveMessages(echoId, cleanedActive);

        const userMsg = mapToUiMessage(response.user_message);

        // Force USER + audio shape so the optimistic msg never collapses to a text/AI bubble.
        userMsg.sender_type = MessageSender.USER;
        userMsg.is_audio_message = true;

        userMsg.localAudioUrl = localUrl;
        userMsg.waveform = waveform;
        userMsg.duration = response.user_message.audio_duration_seconds ?? duration;
        userMsg.has_audio = false;
        userMsg.isAudioGenerated = false;

        const aiMsg = mapToUiMessage(response.ai_response);
        const aiHasAudioUrl = !!(
          aiMsg.audio_url ||
          aiMsg.audio_url_azure ||
          aiMsg.audio_url_elevenlabs
        );
        aiMsg.has_audio = aiHasAudioUrl && !(response.audio_generation_skipped ?? false);
        aiMsg.isAudioGenerated = aiMsg.has_audio;

        updateHistoryCache(echoId, (page) => ({
          ...page,
          messages: [aiMsg, userMsg, ...page.messages.filter((msg) => msg.id !== tempId)],
        }));

        // Two increments: user message + AI response.
        incrementMetaCount(echoId);
        incrementMetaCount(echoId);

        updateLastMessageCache(echoId, aiMsg as SharedMessage);
        analytics.messageSent(echoId, 'echo_chat', 'voice');
        analytics.voiceMessageSent(echoId, duration);
        refreshProfile();

        // Refresh echo stats (total messages, etc.) so StatsRow updates without page reload
        queryClient.invalidateQueries({ queryKey: queryKeys.stats.echo(echoId) });
      } catch (err: unknown) {
        URL.revokeObjectURL(localUrl);

        const activeAfterErr = useChatStore.getState().getActiveMessages(echoId);
        const cleanedActiveErr = activeAfterErr.filter((msg) => msg.id !== aiTempId);
        useChatStore.getState().setActiveMessages(echoId, cleanedActiveErr);

        // Rollback
        updateHistoryCache(echoId, (page) => ({
          ...page,
          messages: page.messages.filter((msg) => msg.id !== tempId),
        }));
        const errorMsg = getErrorMessage(err);
        const isApiError = (e: unknown): e is ApiErrorWithStatus =>
          typeof e === 'object' && e !== null && 'status' in e;
        if (isApiError(err) && err.status === 402) {
          openBillingRecovery(billingRecoveryContext(err, 'VOICE'));
        } else {
          addToast({
            title: 'Voice Message Failed',
            subtitle: errorMsg,
            type: 'error',
          });
        }
        analytics.messageSendFailed(echoId, errorMsg);
      } finally {
        setIsSending(false);
      }
    },
    [
      echoId,
      ttsProvider,
      refreshProfile,
      updateLastMessageCache,
      updateHistoryCache,
      incrementMetaCount,
      queryClient,
      addToast,
      openBillingRecovery,
    ]
  );

  const clearMessages = React.useCallback(() => {
    updateActiveMessages(() => []);
    setError(null);
  }, [updateActiveMessages]);

  const clearConversation = React.useCallback(async () => {
    setIsSending(true);
    setError(null);
    try {
      await api.chat.clearConversation(echoId);
      analytics.conversationCleared(echoId);
      useAudioStore.getState().stop();

      // Clear all state
      updateActiveMessages(() => []);
      // Clear Query Cache for history
      queryClient.setQueryData(queryKeys.chat.history(echoId), () => ({
        pages: [],
        pageParams: [],
      }));
      // Invalidate to ensure clean state
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.history(echoId) });

      queryClient.setQueryData(queryKeys.chat.lastMessage(echoId), null);

      // Reset meta query so greeting effect sees total=0 and pagination uses correct offset
      queryClient.setQueryData(['chat', 'meta', echoId], { total: 0 });
      totalCountRef.current.set(echoId, 0);
      prevMetaTotalRef.current.set(echoId, 0);

      // Allow greeting to fire again for this echo
      useChatStore.getState().clearGreetingSent(echoId);
    } catch (err) {
      addToast({
        title: 'Clear Failed',
        subtitle: getErrorMessage(err),
        type: 'error',
      });
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [echoId, queryClient, updateActiveMessages, addToast]);

  const addLocalMessage = React.useCallback(
    (msg: Message) => {
      updateActiveMessages((prev) => [...prev, msg]);
      updateLastMessageCache(echoId, msg as SharedMessage);
    },
    [echoId, updateLastMessageCache, updateActiveMessages]
  );

  return {
    messages,
    isLoading: (isHistoryLoading && messages.length === 0) || isSending,
    error,
    sendMessage,
    sendAudioMessage,
    clearMessages,
    clearConversation,
    hasMore: !!hasNextPage,
    loadMore: async () => {
      if (hasNextPage) await fetchNextPage();
    },
    addLocalMessage,
  };
}
